import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import IconButton from '@mui/material/IconButton';
import PropTypes from 'prop-types';
import PaginatedAutocomplete from '../../../../shared-components/PaginatedAutocomplete';
import AdmicityGroupFilterSelect from '../../../../shared-components/AdmicityGroupFilterSelect';
import {useLazyGetAllEmployeeProfilesQuery} from '../../../../api/services/employeeService';
import {useLazyGetStudentFiltersQuery} from '../../../../api/services/studentService';
import {GROUP_MEMBERS_TYPE} from '../../../../constants/GroupMembersType';
import {maySaveRestriction} from './restrictionSaveGuard';

/* eslint-disable react/prop-types */

// Maria, 19 October 2026: the restriction box as a wizard in the same style as Add Contact, one question a page
const STEPS = ['who', 'profiles', 'check'];
const QUESTIONS = {
    who: 'Select the member of staff or group of staff',
    profiles: 'Select profiles for access restriction',
    check: 'Check the restriction details'
};
const BLUE_SX = {backgroundColor: '#186BBE', boxShadow: 'none', '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}};

const getEmployeeDisplayName = (employee) =>
    employee?.displayName ||
    `${employee?.preferredForename || ''} ${employee?.preferredSurname || ''}`.trim();

const filterModulesByMembersType = (modules, membersType, nameHints = []) =>
    (modules || [])
        .filter(module =>
            module.membersType === membersType ||
            nameHints.some(hint => module.name?.toLowerCase().includes(hint)))
        .map(({filters, ...rest}) => ({
            ...rest,
            filters: (filters || []).filter(filter =>
                filter.membersType === membersType || filter.membersType == null)
        }))
        .filter(module => module.filters?.length);

const emptyForm = () => ({
    subjectType: 'employee',
    employee: null,
    employeeGroup: null,
    students: [],
    applications: [],
    studentGroups: []
});

const StudentProfileAccessRestrictionDialog = (
    {
        open,
        mode,
        ruleId,
        initialValues,
        existingRules = [],
        groupFilterModules = [],
        isLoading = false,
        isSaving = false,
        onClose,
        onSave
    }) => {
    const [form, setForm] = useState(emptyForm);
    const [step, setStep] = useState('who');
    const [selectedStudentGroupFilter, setSelectedStudentGroupFilter] = useState(null);
    const isEdit = mode === 'edit';

    const employeeGroupModules = useMemo(
        () => filterModulesByMembersType(
            groupFilterModules,
            GROUP_MEMBERS_TYPE.employee,
            ['employees', 'school group', 'my group']
        ),
        [groupFilterModules]
    );

    const studentGroupModules = useMemo(
        () => filterModulesByMembersType(
            groupFilterModules,
            GROUP_MEMBERS_TYPE.students,
            []
        ).filter(module => module.name !== 'EMPLOYEES'),
        [groupFilterModules]
    );

    const existingEmployeeIds = useMemo(
        () => new Set(
            (existingRules || [])
                .filter(rule => rule.subjectType === 'employee' && rule.employeeId)
                .filter(rule => !isEdit || rule.id !== ruleId)
                .map(rule => rule.employeeId)
        ),
        [existingRules, isEdit, ruleId]
    );

    const existingEmployeeGroupFilterIds = useMemo(
        () => new Set(
            (existingRules || [])
                .filter(rule => rule.subjectType === 'employeeGroup' && rule.employeeGroupFilterId)
                .filter(rule => !isEdit || rule.id !== ruleId)
                .map(rule => rule.employeeGroupFilterId)
        ),
        [existingRules, isEdit, ruleId]
    );

    useEffect(() => {
        if (!open) {
            return;
        }

        if (initialValues) {
            setForm({
                subjectType: initialValues.subjectType || 'employee',
                employee: initialValues.employee || null,
                employeeGroup: initialValues.employeeGroup || null,
                students: initialValues.students || [],
                applications: initialValues.applications || [],
                studentGroups: initialValues.studentGroups || []
            });
        } else {
            setForm(emptyForm());
        }

        // the person or group of a saved restriction stays the same, so editing starts at the profiles
        setStep(isEdit ? 'profiles' : 'who');
        setSelectedStudentGroupFilter(null);
    }, [open, initialValues, ruleId, isEdit]);

    const subjectLabel = form.subjectType === 'employee'
        ? getEmployeeDisplayName(form.employee)
        : form.employeeGroup?.name;

    const hasSubject = form.subjectType === 'employee'
        ? Boolean(form.employee?.id)
        : Boolean(form.employeeGroup?.groupFilterId);

    const isDuplicateSubject = form.subjectType === 'employee'
        ? Boolean(form.employee?.id && existingEmployeeIds.has(form.employee.id))
        : Boolean(
            form.employeeGroup?.groupFilterId &&
            existingEmployeeGroupFilterIds.has(form.employeeGroup.groupFilterId)
        );

    const hasExclusions = form.students.length > 0 ||
        form.applications.length > 0 ||
        form.studentGroups.length > 0;

    const hasUnaddedStudentGroup = Boolean(selectedStudentGroupFilter?.filterId);

    const maySave = maySaveRestriction({
        hasSubject,
        hasExclusions,
        isDuplicateSubject,
        hasUnaddedStudentGroup
    });

    const handleClose = () => {
        if (isSaving) {
            return;
        }
        onClose();
    };

    const handleAddStudentGroup = () => {
        if (!selectedStudentGroupFilter?.filterId) {
            return;
        }

        const alreadyAdded = form.studentGroups.some(
            group => group.groupFilterId === selectedStudentGroupFilter.filterId
        );
        if (alreadyAdded) {
            setSelectedStudentGroupFilter(null);
            return;
        }

        const name = selectedStudentGroupFilter.paths?.map(path => path.name).join(' - ') ||
            selectedStudentGroupFilter.paths?.at(-1)?.name ||
            'Student group';

        setForm(prev => ({
            ...prev,
            studentGroups: [
                ...prev.studentGroups,
                {
                    id: selectedStudentGroupFilter.filterId,
                    groupFilterId: selectedStudentGroupFilter.filterId,
                    name
                }
            ]
        }));
        setSelectedStudentGroupFilter(null);
    };

    const handleSave = () => {
        if (!maySave) {
            return;
        }

        onSave({
            ruleId,
            subjectType: form.subjectType,
            employeeId: form.subjectType === 'employee' ? form.employee?.id : null,
            employeeGroupFilterId: form.subjectType === 'employeeGroup'
                ? form.employeeGroup?.groupFilterId
                : null,
            studentIds: [
                ...form.students.map(student => student.id),
                ...form.applications.map(application => application.id)
            ],
            studentGroupFilterIds: form.studentGroups.map(group => group.groupFilterId)
        });
    };

    const firstStep = isEdit ? 'profiles' : 'who';
    const stepNumber = STEPS.indexOf(step) + 1;
    const back = () => (step === firstStep ? handleClose() : setStep(STEPS[stepNumber - 2]));
    const next = () => {
        // a group chosen but not added with 'Add group' is added on Next, so it is never lost
        if (step === 'profiles' && hasUnaddedStudentGroup) {
            handleAddStudentGroup();
        }
        setStep(STEPS[stepNumber]);
    };
    const canGoOn = step === 'who'
        ? hasSubject && !isDuplicateSubject
        : step === 'check' ? maySave : true;

    const whoStep = (
        <Stack spacing={2.5} sx={{width: '100%', maxWidth: 480, mx: 'auto'}}>
            <ToggleButtonGroup
                exclusive
                fullWidth
                color="primary"
                value={form.subjectType}
                onChange={(_event, value) => {
                    if (!value) {
                        return;
                    }
                    setForm(prev => ({
                        ...emptyForm(),
                        subjectType: value,
                        students: prev.students,
                        applications: prev.applications,
                        studentGroups: prev.studentGroups
                    }));
                }}
                data-testid="restriction-subject-type"
            >
                <ToggleButton value="employee" sx={{textTransform: 'none'}}>Member of staff</ToggleButton>
                <ToggleButton value="employeeGroup" sx={{textTransform: 'none'}}>Group of staff</ToggleButton>
            </ToggleButtonGroup>

            {form.subjectType === 'employee' && (
                <PaginatedAutocomplete
                    multiple={false}
                    disablePortal={false}
                    textFieldProps={{
                        label: 'Member of staff',
                        error: isDuplicateSubject,
                        helperText: isDuplicateSubject ? 'A restriction already exists for this member of staff. Please edit the existing restriction.' : ''
                    }}
                    fetchFn={useLazyGetAllEmployeeProfilesQuery}
                    getOptionLabel={getEmployeeDisplayName}
                    value={form.employee}
                    onChange={(_event, selected) => {
                        setForm(prev => ({
                            ...prev,
                            employee: selected
                                ? {
                                    ...selected,
                                    displayName: getEmployeeDisplayName(selected)
                                }
                                : null,
                            employeeGroup: null
                        }));
                    }}
                />
            )}

            {form.subjectType === 'employeeGroup' && (
                <>
                    <AdmicityGroupFilterSelect
                        isRequired={false}
                        sx={{width: '100%'}}
                        data={employeeGroupModules}
                        label="Group of staff"
                        value={form.employeeGroup?.groupFilterId || ''}
                        onChange={(filterId, paths) => {
                            if (!filterId) {
                                setForm(prev => ({
                                    ...prev,
                                    employeeGroup: null,
                                    employee: null
                                }));
                                return;
                            }

                            const name = paths?.map(path => path.name).join(' - ') ||
                                paths?.at(-1)?.name ||
                                'Group of staff';

                            setForm(prev => ({
                                ...prev,
                                employee: null,
                                employeeGroup: {
                                    groupFilterId: filterId,
                                    name
                                }
                            }));
                        }}
                    />
                    {isDuplicateSubject && (
                        <Typography variant="body2" color="error">
                            A restriction already exists for this group of staff. Please edit the existing restriction.
                        </Typography>
                    )}
                </>
            )}
        </Stack>
    );

    const label = text => <Typography sx={{fontWeight: 600, mb: 0.75}}>{text}</Typography>;

    const groupsPart = (
        <Box>
            {label('Student groups')}
            <Stack direction="row" spacing={1} alignItems="center">
                <AdmicityGroupFilterSelect
                    isRequired={false}
                    compact
                    sx={{flex: 1}}
                    data={studentGroupModules}
                    label="Group"
                    value={selectedStudentGroupFilter?.filterId || ''}
                    onChange={(filterId, paths) =>
                        setSelectedStudentGroupFilter(filterId ? {filterId, paths} : null)}
                />
                <Button
                    variant="outlined"
                    disabled={!selectedStudentGroupFilter?.filterId}
                    onClick={handleAddStudentGroup}
                    data-testid="restriction-add-group"
                >
                    Add group
                </Button>
            </Stack>
            {form.studentGroups.map(group => (
                <Stack
                    key={group.groupFilterId}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{borderBottom: '1px solid #eef0f3', py: 0.5}}
                >
                    <Typography>{group.name}</Typography>
                    <IconButton
                        size="small"
                        aria-label="Delete group"
                        onClick={() => setForm(prev => ({
                            ...prev,
                            studentGroups: prev.studentGroups.filter(item => item.groupFilterId !== group.groupFilterId)
                        }))}
                    >
                        <DeleteOutlineRoundedIcon fontSize="small"/>
                    </IconButton>
                </Stack>
            ))}
        </Box>
    );

    const profilesPart = (pupilType, field) => (
        <Box>
            {label(pupilType === 'student' ? 'Individual students' : 'Individual applicants')}
            <PaginatedAutocomplete
                disablePortal={false}
                textFieldProps={{label: pupilType === 'student' ? 'Students' : 'Applicants'}}
                fetchFn={useLazyGetStudentFiltersQuery}
                fetchFnProps={{queryParams: {pupilType}}}
                getOptionLabel={(option) => option.label}
                value={form[field]}
                onChange={(_event, newValue) => setForm(prev => ({...prev, [field]: newValue || []}))}
            />
        </Box>
    );

    const listOrDash = items => (items.length ? items.join(', ') : '-');
    const checkRows = [
        [form.subjectType === 'employee' ? 'Member of staff' : 'Group of staff', subjectLabel || '-'],
        ['Excluded student groups', listOrDash(form.studentGroups.map(group => group.name))],
        ['Excluded students', listOrDash(form.students.map(student => student.label))],
        ['Excluded applicants', listOrDash(form.applications.map(application => application.label))]
    ];

    const checkStep = (
        <Box>
            <Table size="small" data-testid="restriction-check">
                <TableBody>
                    {checkRows.map(([label, value]) => (
                        <TableRow key={label}>
                            <TableCell sx={{fontWeight: 600, width: '35%', verticalAlign: 'top'}}>{label}</TableCell>
                            <TableCell>{value}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {!hasExclusions && (
                <Typography variant="body2" color="error" sx={{mt: 2}}>
                    This restriction cannot be saved until at least one student group, student or applicant has been excluded.
                </Typography>
            )}
        </Box>
    );

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            onClose={handleClose}
            data-testid="restriction-dialog"
            PaperProps={{sx: {borderRadius: 3, height: 'min(600px, calc(100% - 64px))'}}}
        >
            <Box sx={{px: 4, pt: 2, pb: 1.5, borderBottom: '1px solid #eef0f3'}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="overline" sx={{color: '#0DAD95', fontWeight: 700, letterSpacing: 1}}>
                        {isEdit ? 'Edit Restriction' : 'Add Restriction'}
                    </Typography>
                    <IconButton size="small" onClick={handleClose} aria-label="Close">
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </Stack>
                <Typography variant="h5" sx={{fontWeight: 600}} data-testid="restriction-question">
                    {QUESTIONS[step]}
                </Typography>
                <Stack direction="row" spacing={1} sx={{mt: 1.5}}>
                    {STEPS.map((stepName, index) => (
                        <Box key={stepName} sx={{flex: 1, height: 4, borderRadius: 2, backgroundColor: index < stepNumber ? '#186BBE' : '#e3e8ef'}}/>
                    ))}
                </Stack>
            </Box>

            <DialogContent sx={{px: 4, pt: 3, pb: 3}}>
                {isLoading
                    ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={32}/>
                        </Box>
                    )
                    : (
                        <>
                            {step === 'who' && whoStep}
                            {step === 'profiles' && (
                                <Stack spacing={3} sx={{width: '100%', maxWidth: 480, mx: 'auto'}}>
                                    {groupsPart}
                                    {profilesPart('student', 'students')}
                                    {profilesPart('applicant', 'applications')}
                                </Stack>
                            )}
                            {step === 'check' && checkStep}
                        </>
                    )}
            </DialogContent>

            <Box sx={{px: 4, py: 2, borderTop: '1px solid #eef0f3'}}>
                <Stack direction="row" justifyContent="space-between">
                    <Button data-testid="restriction-back" variant="outlined" onClick={back} disabled={isSaving}>Back</Button>
                    <Button
                        data-testid={step === 'check' ? 'restriction-save' : 'restriction-next'}
                        variant="contained"
                        onClick={step === 'check' ? handleSave : next}
                        disabled={isSaving || isLoading || !canGoOn}
                        sx={BLUE_SX}
                    >
                        {step === 'check' ? (isSaving ? 'Saving...' : 'Save') : 'Next'}
                    </Button>
                </Stack>
            </Box>
        </Dialog>
    );
};

StudentProfileAccessRestrictionDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(['create', 'edit']).isRequired,
    ruleId: PropTypes.number,
    initialValues: PropTypes.object,
    existingRules: PropTypes.array,
    groupFilterModules: PropTypes.array,
    isLoading: PropTypes.bool,
    isSaving: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
};

export default StudentProfileAccessRestrictionDialog;
